import { AlertTriangle, Edit, History, Package, Search, Trash2 } from "lucide-react";
import { MATERIAL_CATEGORIES } from "..";

const StockRegisterSection = ({ ctx }) => {
  const {
    fetchRateHistory,
    handleDelete,
    handleEdit,
    isMasterAdmin,
    searchTerm,
    selectedCategory,
    setSearchTerm,
    setSelectedCategory,
    stockSummary
  } = ctx;
  return (
    <>
        <div>
          {/* Search and Category Filter */}
          <div className="flex flex-wrap items-center gap-4 mb-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search materials by name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary text-sm"
                data-testid="material-search-input"
              />
            </div>
            <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setSelectedCategory("all")}
              className={`px-4 py-2 text-sm font-medium rounded-sm transition-colors ${
                selectedCategory === "all" ? 'bg-primary text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              All Materials
            </button>
            {MATERIAL_CATEGORIES.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`px-4 py-2 text-sm font-medium rounded-sm transition-colors ${
                  selectedCategory === cat.id ? `bg-${cat.color} text-white` : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {cat.label}
              </button>
            ))}
            </div>
          </div>
          
          <div className="stat-card overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b-2 border-slate-300">
                <th className="text-left py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Material</th>
                <th className="text-left py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Category</th>
                <th className="text-right py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Unit</th>
                <th className="text-right py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Opening</th>
                <th className="text-right py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Total In</th>
                <th className="text-right py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Total Out</th>
                <th className="text-right py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Current</th>
                <th className="text-right py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Min Stock</th>
                <th className="text-right py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Rate (₹)</th>
                <th className="text-right py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Value</th>
                {isMasterAdmin && (
                  <th className="text-center py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Actions</th>
                )}
              </tr>
            </thead>
            <tbody>
              {stockSummary
                .filter(item => selectedCategory === "all" || item.category === selectedCategory)
                .filter(item => !searchTerm || item.name.toLowerCase().includes(searchTerm.toLowerCase()))
                .map((item) => {
                  const categoryInfo = MATERIAL_CATEGORIES.find(c => c.id === item.category) || { label: "Construction Materials", color: "primary" };
                  return (
                <tr key={item.id} className={`border-b border-slate-200 hover:bg-slate-50 ${item.low_stock_alert ? 'bg-red-50' : ''}`} data-testid={`stock-row-${item.id}`}>
                  <td className="py-3 px-4 font-medium text-primary">
                    {item.name}
                    {item.low_stock_alert && <AlertTriangle className="w-4 h-4 inline ml-2 text-error" />}
                  </td>
                  <td className="py-3 px-4">
                    <span className={`px-2 py-1 text-xs rounded-full bg-${categoryInfo.color}/10 text-${categoryInfo.color}`}>
                      {categoryInfo.label}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-right font-mono text-sm">{item.unit}</td>
                  <td className="py-3 px-4 text-right font-mono text-sm">{item.opening_stock?.toFixed(2)}</td>
                  <td className="py-3 px-4 text-right font-mono text-sm text-success font-semibold">+{item.total_inward?.toFixed(2)}</td>
                  <td className="py-3 px-4 text-right font-mono text-sm text-warning font-semibold">-{item.total_outward?.toFixed(2)}</td>
                  <td className={`py-3 px-4 text-right font-mono text-sm font-bold ${item.low_stock_alert ? 'text-error' : 'text-primary'}`}>
                    {item.current_stock?.toFixed(2)}
                  </td>
                  <td className="py-3 px-4 text-right font-mono text-sm">{item.minimum_stock?.toFixed(2)}</td>
                  <td className="py-3 px-4 text-right font-mono text-sm">
                    <div className="flex items-center justify-end space-x-2">
                      <span>{item.rate?.toFixed(2)}</span>
                      <button 
                        onClick={() => fetchRateHistory(item.id)} 
                        data-testid={`rate-history-${item.id}`}
                        className="p-1 hover:bg-slate-200 rounded" 
                        title="View Rate History"
                      >
                        <History className="w-3 h-3 text-slate-500 hover:text-primary" />
                      </button>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-right font-mono text-sm font-bold">₹{item.stock_value?.toLocaleString('en-IN')}</td>
                  {isMasterAdmin && (
                    <td className="py-3 px-4 text-center">
                      <div className="flex items-center justify-center space-x-2">
                        <button onClick={() => handleEdit(item)} data-testid={`edit-material-${item.id}`} className="p-1 hover:bg-slate-200 rounded" title="Edit">
                          <Edit className="w-4 h-4 text-primary" />
                        </button>
                        <button onClick={() => handleDelete(item.id, item.name)} data-testid={`delete-material-${item.id}`} className="p-1 hover:bg-red-100 rounded" title="Delete">
                          <Trash2 className="w-4 h-4 text-error" />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
                  );
                })}
            </tbody>
          </table>
          {stockSummary.filter(item => selectedCategory === "all" || item.category === selectedCategory).filter(item => !searchTerm || item.name.toLowerCase().includes(searchTerm.toLowerCase())).length === 0 && (
            <div className="text-center py-12 text-slate-500">
              <Package className="w-16 h-16 mx-auto mb-4 text-slate-400" />
              {searchTerm ? `No materials found matching "${searchTerm}"` : 
               selectedCategory === "all" ? "No materials added yet." : `No ${MATERIAL_CATEGORIES.find(c => c.id === selectedCategory)?.label || 'materials'} added yet.`}
            </div>
          )}
        </div>
        </div>
    </>
  );
};

export default StockRegisterSection;
